import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { notificar } from '../utils/notify';
import '../styles/fmp.css';
import '../styles/fmpcustom.css';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(parseFloat(valor));
};

// ========================================
// COMPONENTE LINHA EDITÁVEL - INPUT MANUAL
// ========================================
const MovimentoRow = ({ movimento, editando, onSalvar, mostrarProjeto }) => {
  const [projetoDigitado, setProjetoDigitado] = useState('');
  const [salvando, setSalvando] = useState(false);

  const handleSalvar = async () => {
    if (!projetoDigitado || projetoDigitado.trim() === '') {
      alert('Digite o nome do projeto');
      return;
    }

    if (!movimento.nCodLancamento) {
      alert('Este movimento não possui código de lançamento. Não é possível atualizar.');
      return;
    }

    setSalvando(true);
    await onSalvar(movimento.nCodLancamento, projetoDigitado.trim());
    setSalvando(false);
    setProjetoDigitado('');
  };

  return (
    <tr>
      {/* NOVA COLUNA: NÚMERO (APENAS PARA VALORES A RECEBER) */}
      {mostrarProjeto && (
        <td><small className="text-muted"><strong>{movimento.numero || 'N/A'}</strong></small></td>
      )}
      <td><small>{movimento.data}</small></td>
      <td><small>{movimento.descricao}</small></td>
      {/* MOSTRAR PROJETO SE FOR VALORES A RECEBER */}
      {mostrarProjeto && (
        <td><small className="text-primary"><strong>{movimento.projeto_nome || 'N/A'}</strong></small></td>
      )}
      <td><small className="text-muted">{movimento.categoria}</small></td>
      <td><small className="text-muted">{movimento.cliente}</small></td>
      <td className="text-end">
        <strong className={movimento.tipo === 'RECEITA' ? 'text-success' : 'text-danger'}>
          {formatarValor(Math.abs(movimento.valor))}
        </strong>
      </td>
      <td className="text-center">
        <span className={`badge badge-sm ${movimento.tipo === 'RECEITA' ? 'bg-success' : 'bg-danger'}`}>
          {movimento.tipo === 'RECEITA' ? 'REC' : 'DESP'}
        </span>
      </td>
      {/* COLUNA STATUS */}
      <td className="text-center">
        <span className={`badge badge-sm ${movimento.status === 'CONCILIADO' ? 'bg-success' : 'bg-warning text-dark'}`}>
          {movimento.status === 'CONCILIADO' ? 'CONCILIADO' : 'PREVISTO'}
        </span>
      </td>
      {editando && (
        <td>
          {movimento.nCodLancamento ? (
            <div className="input-group input-group-sm">
              <input 
                type="text"
                className="form-control form-control-sm"
                placeholder="Digite o nome do projeto..."
                value={projetoDigitado}
                onChange={(e) => setProjetoDigitado(e.target.value)}
                disabled={salvando}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && projetoDigitado.trim()) {
                    handleSalvar();
                  }
                }}
              />
              <button 
                className="btn btn-success btn-sm"
                onClick={handleSalvar}
                disabled={!projetoDigitado.trim() || salvando}
                title="Salvar projeto"
              >
                {salvando ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  <i className="fas fa-save"></i>
                )}
              </button>
            </div>
          ) : (
            <small className="text-muted">Sem código</small>
          )}
        </td>
      )}
    </tr>
  );
};

const ProjetosErnedu = () => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');

  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [filtroProjeto, setFiltroProjeto] = useState('');
  const [modalDetalhes, setModalDetalhes] = useState(false);
  const [projetoDetalhes, setProjetoDetalhes] = useState(null);
  const [carregandoDetalhes, setCarregandoDetalhes] = useState(false);
  
  // ESTADOS PARA FILTROS DA TABELA
  const [filtroNomeProjeto, setFiltroNomeProjeto] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [ordenacao, setOrdenacao] = useState({ coluna: 'data_fim', direcao: 'desc' });
  
  // ESTADO PARA EDIÇÃO DE PROJETOS
  const [editandoProjeto, setEditandoProjeto] = useState(false);
  
  const navigate = useNavigate();
  const isFirstRender = useRef(true);
  const isLoading = useRef(false);

  const COLORS = ['#1e88e5', '#28a745', '#e53935', '#9c27b0', '#ff9800', '#00bcd4', '#795548', '#607d8b'];

  // ========================================
  // CARREGAR DADOS
  // ========================================
  const carregarDados = async (dataInicio = null, dataFim = null, projeto = null) => {
    if (carregando || isLoading.current) {
      console.log('Já está carregando');
      return;
    }

    isLoading.current = true;
    setCarregando(true);

    const token = localStorage.getItem('access');

    try {
      let url = `${process.env.REACT_APP_API_BASE}/api/projetos-ernedu/`;
      const params = new URLSearchParams();
      
      if (dataInicio && dataFim) {
        params.append('data_inicio', dataInicio);
        params.append('data_fim', dataFim);
        console.log(`Aplicando filtro de data: ${dataInicio} - ${dataFim}`);
      } else {
        console.log('Buscando TODOS os projetos (sem filtro de data)');
      }
      
      if (projeto) {
        params.append('projeto', projeto);
        console.log(`Filtrando por projeto: ${projeto}`);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log(`URL final: ${url}`);

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 90000
      });

      if (response.data.success) {
        setDados(response.data.dados);
        setMensagem('Dados carregados com sucesso!');
        
        if (response.data.dados.periodo.data_real_inicio) {
          console.log(`Período real dos dados: ${response.data.dados.periodo.data_real_inicio} - ${response.data.dados.periodo.data_real_fim}`);
        }
        
        setTimeout(() => setMensagem(''), 3000);
      } else {
        setMensagem('Erro ao carregar projetos');
      }

    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
      
      if (error.response?.status === 401) {
        setMensagem('Sessão expirada. Redirecionando...');
        setTimeout(() => navigate('/login'), 2000);
      } else if (error.response?.status === 404) {
        setMensagem('Nenhum projeto encontrado');
      } else {
        setMensagem('Erro ao carregar dados');
      }
    } finally {
      setCarregando(false);
      isLoading.current = false;
    }
  };

  // ========================================
  // CARREGAR DETALHES DO PROJETO
  // ========================================
  const carregarDetalhesProjeto = async (nomeProjeto) => {
    setCarregandoDetalhes(true);
    const token = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/detalhes-projeto-ernedu/?projeto=${encodeURIComponent(nomeProjeto)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 30000
        }
      );

      if (response.data.success) {
        setProjetoDetalhes(response.data.dados);
        setModalDetalhes(true);
      }

    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      setMensagem('Erro ao carregar detalhes do projeto');
    } finally {
      setCarregandoDetalhes(false);
    }
  };

  // ========================================
  // NOVO: CARREGAR TODOS OS MOVIMENTOS PREVISTOS (A RECEBER)
  // ========================================
  const carregarMovimentosNaoRecebidos = async () => {
    setCarregandoDetalhes(true);
    const token = localStorage.getItem('access');

    try {
      setMensagem('Carregando valores a receber...');
      
      // Buscar detalhes de TODOS os projetos
      let url = `${process.env.REACT_APP_API_BASE}/api/projetos-ernedu/`;
      const params = new URLSearchParams();
      
      if (filtroDataInicio && filtroDataFim) {
        params.append('data_inicio', filtroDataInicio);
        params.append('data_fim', filtroDataFim);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 90000
      });

      if (response.data.success) {
        const todosMovimentosPrevistos = [];
        
        // Para cada projeto, buscar seus movimentos previstos
        const projetosComPrevistos = response.data.dados.projetos.filter(p => p.receitas_previstas > 0);
        
        console.log(`Buscando detalhes de ${projetosComPrevistos.length} projetos com valores a receber...`);
        
        for (const projeto of projetosComPrevistos) {
          // Buscar detalhes do projeto
          const detalhesUrl = `${process.env.REACT_APP_API_BASE}/api/detalhes-projeto-ernedu/?projeto=${encodeURIComponent(projeto.projeto)}`;
          
          try {
            const detalhesResponse = await axios.get(detalhesUrl, {
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 30000
            });
            
            if (detalhesResponse.data.success) {
              // Filtrar apenas movimentos PREVISTOS e RECEITAS
              const movimentosPrevistos = detalhesResponse.data.dados.movimentos
                .filter(mov => mov.status === 'PREVISTO' && mov.tipo === 'RECEITA')
                .map(mov => ({
                  ...mov,
                  projeto_nome: projeto.projeto  // Adicionar nome do projeto
                }));
              
              todosMovimentosPrevistos.push(...movimentosPrevistos);
            }
          } catch (err) {
            console.error(`Erro ao buscar detalhes do projeto ${projeto.projeto}:`, err);
          }
        }
        
        // Ordenar por data (mais recente primeiro)
        todosMovimentosPrevistos.sort((a, b) => {
          const dateA = new Date(a.data.split('/').reverse().join('-'));
          const dateB = new Date(b.data.split('/').reverse().join('-'));
          return dateB - dateA;
        });
        
        // Calcular resumo
        const totalPrevisto = todosMovimentosPrevistos.reduce((acc, mov) => acc + mov.valor, 0);
        
        setProjetoDetalhes({
          projeto: 'Valores a Receber (Previstos)',
          movimentos: todosMovimentosPrevistos,
          evolucao: [],
          resumo: {
            total_receitas: totalPrevisto,
            total_despesas: 0,
            saldo: totalPrevisto,
            margem: 100,
            qtd_receitas: todosMovimentosPrevistos.length,
            qtd_despesas: 0,
            qtd_movimentos: todosMovimentosPrevistos.length,
            periodo_inicio: response.data.dados.periodo.data_real_inicio,
            periodo_fim: response.data.dados.periodo.data_real_fim
          }
        });
        
        setModalDetalhes(true);
        setMensagem(`${todosMovimentosPrevistos.length} movimentos a receber encontrados`);
        setTimeout(() => setMensagem(''), 3000);
      }

    } catch (error) {
      console.error('Erro ao carregar movimentos não recebidos:', error);
      setMensagem('Erro ao carregar valores a receber');
    } finally {
      setCarregandoDetalhes(false);
    }
  };

  // ========================================
  // ATUALIZAR NOME DO PROJETO
  // ========================================
  const atualizarNomeProjeto = async (nCodLancamento, novoNome) => {
    if (!novoNome || novoNome.trim() === '') {
      setMensagem('Digite um nome válido para o projeto');
      return;
    }

    if (!window.confirm(`Confirma a alteração do projeto para "${novoNome}"?\n\nIsso atualizará na Omie e no banco de dados.`)) {
      return;
    }

    const token = localStorage.getItem('access');

    try {
      setMensagem('Atualizando projeto na Omie...');

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/atualizar-projeto-omie/`,
        {
          nCodLancamento: nCodLancamento,
          novo_nome_projeto: novoNome
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      if (response.data.success) {
        setMensagem('Projeto atualizado com sucesso na Omie!');
        
        setTimeout(() => {
          carregarDados();
          setModalDetalhes(false);
          setEditandoProjeto(false);
        }, 2000);
      } else {
        setMensagem(`${response.data.erro}`);
      }

    } catch (error) {
      console.error('Erro ao atualizar projeto:', error);
      
      if (error.response?.data?.erro) {
        setMensagem(`${error.response.data.erro}`);
      } else {
        setMensagem('Erro ao atualizar projeto na Omie');
      }
    }
  };

  // ========================================
  // FILTROS
  // ========================================
  const aplicarFiltros = () => {
    if (filtroDataInicio && filtroDataFim) {
      carregarDados(filtroDataInicio, filtroDataFim, filtroProjeto);
    } else if (filtroProjeto) {
      carregarDados(null, null, filtroProjeto);
    } else {
      setMensagem('Selecione as datas ou um projeto');
    }
  };

  const limparFiltros = () => {
    setFiltroDataInicio('');
    setFiltroDataFim('');
    setFiltroProjeto('');
    carregarDados();
  };

  const setPeriodoRapido = (tipo) => {
    const hoje = new Date();
    let inicio, fim;
    
    switch(tipo) {
      case 'mes_atual':
        inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        fim = hoje;
        break;
      case 'mes_anterior':
        inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
        fim = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
        break;
      case '3_meses':
        inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1);
        fim = hoje;
        break;
      case '6_meses':
        inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 5, 1);
        fim = hoje;
        break;
      case 'ano_2026':
        inicio = new Date(2026, 0, 1);
        fim = new Date(2026, 11, 31);
        break;
      default:
        return;
    }
    
    setFiltroDataInicio(inicio.toISOString().split('T')[0]);
    setFiltroDataFim(fim.toISOString().split('T')[0]);
  };

  // ========================================
  // FUNÇÕES DE FILTRO E ORDENAÇÃO DA TABELA
  // ========================================
  const filtrarProjetos = () => {
    if (!dados?.projetos) return [];
    
    let projetosFiltrados = [...dados.projetos];
    
    if (filtroNomeProjeto) {
      projetosFiltrados = projetosFiltrados.filter(p => 
        p.projeto.toLowerCase().includes(filtroNomeProjeto.toLowerCase())
      );
    }
    
    if (filtroCliente) {
      projetosFiltrados = projetosFiltrados.filter(p => 
        p.principal_cliente.toLowerCase().includes(filtroCliente.toLowerCase())
      );
    }
    
    return projetosFiltrados;
  };

  const ordenarProjetos = (projetos) => {
    if (!projetos.length) return [];
    
    return [...projetos].sort((a, b) => {
      let valorA, valorB;
      
      switch(ordenacao.coluna) {
        case 'projeto':
          valorA = a.projeto.toLowerCase();
          valorB = b.projeto.toLowerCase();
          break;
        case 'receitas':
          valorA = a.receitas;
          valorB = b.receitas;
          break;
        case 'receitas_recebidas':
          valorA = a.receitas_recebidas;
          valorB = b.receitas_recebidas;
          break;
        case 'receitas_previstas':
          valorA = a.receitas_previstas;
          valorB = b.receitas_previstas;
          break;
        case 'despesas':
          valorA = a.despesas;
          valorB = b.despesas;
          break;
        case 'saldo':
          valorA = a.saldo;
          valorB = b.saldo;
          break;
        case 'margem':
          valorA = a.margem;
          valorB = b.margem;
          break;
        case 'percentual_recebido':
          valorA = a.percentual_recebido;
          valorB = b.percentual_recebido;
          break;
        case 'data_fim':
          valorA = new Date(a.data_fim.split('/').reverse().join('-')).getTime();
          valorB = new Date(b.data_fim.split('/').reverse().join('-')).getTime();
          break;
        default:
          valorA = a.projeto;
          valorB = b.projeto;
      }
      
      if (ordenacao.direcao === 'asc') {
        return valorA > valorB ? 1 : -1;
      } else {
        return valorA < valorB ? 1 : -1;
      }
    });
  };

  const handleOrdenacao = (coluna) => {
    setOrdenacao(prev => ({
      coluna: coluna,
      direcao: prev.coluna === coluna && prev.direcao === 'desc' ? 'asc' : 'desc'
    }));
  };

  const projetosFiltrados = ordenarProjetos(filtrarProjetos());

  // ========================================
  // USEEFFECT INICIAL
  // ========================================
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      console.log('Carregando TODOS os projetos (sem filtro de data)...');
      carregarDados();
    }
  }, []);

  // ========================================
  // CUSTOM TOOLTIP
  // ========================================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#002058',
          border: '1px solid #002058',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0, 32, 88, 0.4)'
        }}>
          <p style={{
            color: '#ffffff',
            margin: '0 0 8px 0',
            fontWeight: 600,
            fontSize: '13px'
          }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{
              color: '#ffffff',
              margin: '4px 0',
              fontWeight: 500,
              fontSize: '14px'
            }}>
              {entry.name}: <span style={{ 
                color: entry.color, 
                fontWeight: 600,
                marginLeft: '8px'
              }}>
                {formatarValor(entry.value)}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="das-page fmp-brand-page">
      <div className="container-fluid mt-4">
        {/* HEADER */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="mb-3">
                      <span className="badge bg-light text-dark me-2" style={{fontSize: '18px', fontWeight: '500'}}>
                        Gestão de Projetos
                      </span>
                      <span className="badge bg-info" style={{fontSize: '13px'}}>
                        <i className="fas fa-chart-line me-1"></i>
                        Análise Financeira
                      </span>
                    </div>
                    
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-project-diagram header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">Controle de Projetos ERNEDU</h2>
                    </div>
                    
                    {dados?.periodo && (
                      <small className="opacity-75 d-block">
                        <i className="fas fa-calendar me-1"></i>
                        {dados.periodo.filtro_aplicado ? (
                          <>Período filtrado: {dados.periodo.inicio} - {dados.periodo.fim}</>
                        ) : (
                          <>Todos os projetos: {dados.periodo.data_real_inicio} - {dados.periodo.data_real_fim}</>
                        )}
                        {dados.estatisticas && (
                          <>
                            <span className="ms-3">
                              <i className="fas fa-folder me-1"></i>
                              {dados.estatisticas.total_projetos} projetos
                            </span>
                          </>
                        )}
                      </small>
                    )}
                  </div>
                  
                  <div>
                    <button 
                      className="btn btn-light btn-sm"
                      onClick={() => navigate('/dashboard-cliente/2')}
                      title="Voltar ao Dashboard"
                    >
                      <i className="fas fa-arrow-left me-1"></i>
                      Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* FILTROS */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-filter me-2 text-primary"></i>
                  Filtros de Pesquisa
                </h5>
              </div>
              <div className="card-body">
                {/* Botões Rápidos */}
                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label text-muted small fw-semibold">
                      <i className="fas fa-clock me-2"></i>
                      Períodos Rápidos
                    </label>
                    <div className="btn-group w-100" role="group">
                      <button 
                        type="button" 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setPeriodoRapido('mes_atual')}
                      >
                        <i className="fas fa-calendar-day me-1"></i>
                        Mês Atual
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setPeriodoRapido('mes_anterior')}
                      >
                        <i className="fas fa-arrow-left me-1"></i>
                        Mês Anterior
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setPeriodoRapido('3_meses')}
                      >
                        <i className="fas fa-calendar-alt me-1"></i>
                        3 Meses
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setPeriodoRapido('6_meses')}
                      >
                        <i className="fas fa-calendar me-1"></i>
                        6 Meses
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-success btn-sm"
                        onClick={() => setPeriodoRapido('ano_2026')}
                      >
                        <i className="fas fa-calendar-check me-1"></i>
                        Ano 2026
                      </button>
                    </div>
                  </div>
                </div>

                {/* Filtros Principais */}
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">
                      <i className="fas fa-calendar-alt me-1"></i>
                      Data Início
                    </label>
                    <input 
                      type="date" 
                      className="form-control"
                      value={filtroDataInicio}
                      onChange={(e) => setFiltroDataInicio(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      <i className="fas fa-calendar-check me-1"></i>
                      Data Fim
                    </label>
                    <input 
                      type="date" 
                      className="form-control"
                      value={filtroDataFim}
                      onChange={(e) => setFiltroDataFim(e.target.value)}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      <i className="fas fa-project-diagram me-1"></i>
                      Projeto Específico
                    </label>
                    <select 
                      className="form-select"
                      value={filtroProjeto}
                      onChange={(e) => setFiltroProjeto(e.target.value)}
                    >
                      <option value="">Todos os Projetos</option>
                      {dados?.lista_projetos?.map((proj, idx) => (
                        <option key={idx} value={proj}>{proj}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2">
                    <label className="form-label text-white">.</label>
                    <div className="d-grid gap-2">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={aplicarFiltros}
                      >
                        <i className="fas fa-search me-1"></i>
                        Filtrar
                      </button>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={limparFiltros}
                      >
                        <i className="fas fa-redo me-1"></i>
                        Limpar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* LOADING */}
        {carregando && !dados && (
          <div className="row">
            <div className="col-12">
              <div className="text-center mt-5 mb-5">
                <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}>
                  <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-3 text-muted">
                  <i className="fas fa-chart-line me-2"></i>
                  Analisando projetos...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CONTEÚDO PRINCIPAL */}
        {dados && (
          <>
            {/* CARDS DE RESUMO */}
            <div className="row mb-4">
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="text-primary me-2">
                        <i className="fas fa-folder-open fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Total de Projetos</h6>
                      </div>
                    </div>
                    <h4 className="text-primary mb-1">
                      {dados.estatisticas.total_projetos}
                    </h4>
                    <small className="text-muted">
                      Todos os períodos
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="text-success me-2">
                        <i className="fas fa-arrow-up fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Total Receitas</h6>
                      </div>
                    </div>
                    <h4 className="text-success mb-1">
                      {formatarValor(dados.estatisticas.total_receitas)}
                    </h4>
                    <small className="text-muted">
                      Ticket médio: {formatarValor(dados.estatisticas.ticket_medio)}
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="text-danger me-2">
                        <i className="fas fa-arrow-down fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Total Despesas</h6>
                      </div>
                    </div>
                    <h4 className="text-danger mb-1">
                      {formatarValor(dados.estatisticas.total_despesas)}
                    </h4>
                    <small className="text-muted">
                      Custo operacional
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className={`${dados.estatisticas.saldo_total >= 0 ? 'text-success' : 'text-danger'} me-2`}>
                        <i className="fas fa-balance-scale fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Saldo Total</h6>
                      </div>
                    </div>
                    <h4 className={`${dados.estatisticas.saldo_total >= 0 ? 'text-success' : 'text-danger'} mb-1`}>
                      {formatarValor(dados.estatisticas.saldo_total)}
                    </h4>
                    <small className={dados.estatisticas.margem_geral >= 0 ? 'text-success' : 'text-danger'}>
                      Margem: {dados.estatisticas.margem_geral.toFixed(1)}%
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* PROJETOS DESTAQUE */}
            {dados.estatisticas.projeto_mais_lucrativo && (
              <div className="row mb-4">
                <div className="col-md-4 mb-3">
                  <div className="card border-0 shadow-sm h-100 border-start border-success border-4">
                    <div className="card-body">
                      <h6 className="text-muted small mb-2">
                        <i className="fas fa-trophy text-success me-1"></i>
                        Projeto Mais Lucrativo
                      </h6>
                      <h5 className="text-success mb-1">{dados.estatisticas.projeto_mais_lucrativo.projeto}</h5>
                      <div className="row mt-3">
                        <div className="col-6">
                          <small className="text-muted d-block">Receitas</small>
                          <strong className="text-success">{formatarValor(dados.estatisticas.projeto_mais_lucrativo.receitas)}</strong>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">Margem</small>
                          <strong className="text-info">{dados.estatisticas.projeto_mais_lucrativo.margem.toFixed(1)}%</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="card border-0 shadow-sm h-100 border-start border-primary border-4">
                    <div className="card-body">
                      <h6 className="text-muted small mb-2">
                        <i className="fas fa-chart-line text-primary me-1"></i>
                        Maior Receita
                      </h6>
                      <h5 className="text-primary mb-1">{dados.estatisticas.projeto_maior_receita.projeto}</h5>
                      <div className="row mt-3">
                        <div className="col-6">
                          <small className="text-muted d-block">Receitas</small>
                          <strong className="text-success">{formatarValor(dados.estatisticas.projeto_maior_receita.receitas)}</strong>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">Movimentos</small>
                          <strong className="text-info">{dados.estatisticas.projeto_maior_receita.qtd_movimentos}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="card border-0 shadow-sm h-100 border-start border-warning border-4">
                    <div className="card-body">
                      <h6 className="text-muted small mb-2">
                        <i className="fas fa-exclamation-triangle text-warning me-1"></i>
                        Atenção Requerida
                      </h6>
                      <h5 className="text-warning mb-1">{dados.estatisticas.projeto_menos_lucrativo.projeto}</h5>
                      <div className="row mt-3">
                        <div className="col-6">
                          <small className="text-muted d-block">Saldo</small>
                          <strong className="text-danger">{formatarValor(dados.estatisticas.projeto_menos_lucrativo.saldo)}</strong>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">Margem</small>
                          <strong className="text-danger">{dados.estatisticas.projeto_menos_lucrativo.margem.toFixed(1)}%</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SEÇÃO: SEM DADOS DO PROJETO + GRÁFICO */}
            <div className="row mb-4">
              {/* RECEITAS SEM PROJETO CADASTRADO */}
              <div className="col-lg-8 mb-3">
                <div className="card border-0 shadow-sm h-100 border-3" style={{ borderColor: '#002058' }}>
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Receitas SEM Projeto Cadastrado
                    </h5>
                  </div>
                  <div className="card-body">
                    {(() => {
                      const projetoSemDados = dados.projetos.find(p => p.projeto === 'Sem dados do projeto');
                      
                      if (!projetoSemDados) {
                        return (
                          <div className="text-center py-5">
                            <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                            <h5 className="text-success">Excelente! </h5>
                            <p className="text-muted">Todas as receitas possuem projetos cadastrados.</p>
                          </div>
                        );
                      }
                      
                      return (
                        <>
                          {/* CARDS DE RESUMO */}
                          <div className="row mb-3">
                            <div className="col-md-3 mb-2">
                              <div className="card border-0 bg-light h-100">
                                <div className="card-body p-3">
                                  <small className="text-muted d-block mb-1">Receitas Total</small>
                                  <h5 className="text-primary mb-0">{formatarValor(projetoSemDados.receitas)}</h5>
                                  <small className="text-muted">{projetoSemDados.qtd_receitas} movimentos</small>
                                </div>
                              </div>
                            </div>
                            
                            <div className="col-md-3 mb-2">
                              <div className="card border-0 bg-light h-100">
                                <div className="card-body p-3">
                                  <small className="text-muted d-block mb-1">Recebido</small>
                                  <h5 className="text-success mb-0">{formatarValor(projetoSemDados.receitas_recebidas)}</h5>
                                  <small className="text-muted">{projetoSemDados.qtd_receitas_recebidas} mov.</small>
                                </div>
                              </div>
                            </div>
                            
                            <div className="col-md-3 mb-2">
                              <div className="card border-0 bg-light h-100">
                                <div className="card-body p-3">
                                  <small className="text-muted d-block mb-1">Previsto</small>
                                  <h5 className="text-warning mb-0">{formatarValor(projetoSemDados.receitas_previstas)}</h5>
                                  <small className="text-muted">{projetoSemDados.qtd_receitas_previstas} mov.</small>
                                </div>
                              </div>
                            </div>
                            
                            <div className="col-md-3 mb-2">
                              <div className="card border-0 bg-light h-100">
                                <div className="card-body p-3">
                                  <small className="text-muted d-block mb-1">% Recebido</small>
                                  <h5 className="text-info mb-0">{projetoSemDados.percentual_recebido.toFixed(1)}%</h5>
                                  <small className="text-muted">do total</small>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* BARRA DE PROGRESSO */}
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <small className="text-muted">Progresso de Conciliação</small>
                              <small className="text-success fw-bold">{projetoSemDados.percentual_recebido.toFixed(1)}%</small>
                            </div>
                            <div className="progress" style={{height: '25px'}}>
                              <div 
                                className="progress-bar bg-success" 
                                role="progressbar" 
                                style={{width: `${projetoSemDados.percentual_recebido}%`}}
                              >
                                <strong>{formatarValor(projetoSemDados.receitas_recebidas)}</strong>
                              </div>
                            </div>
                          </div>

                          {/* BOTÕES */}
                          <div className="d-grid gap-2">
                            <button 
                              className="btn btn-warning"
                              onClick={() => carregarDetalhesProjeto('Sem dados do projeto')}
                              disabled={carregandoDetalhes}
                            >
                              {carregandoDetalhes ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Carregando...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-eye me-2"></i>
                                  Ver Todos os Movimentos Detalhados
                                </>
                              )}
                            </button>

                            {/* BOTÃO SUTIL: VALORES A RECEBER */}
                            <button 
                              className="btn btn-sm btn-outline-warning"
                              onClick={carregarMovimentosNaoRecebidos}
                              disabled={carregandoDetalhes}
                            >
                              {carregandoDetalhes ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Carregando...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-hourglass-half me-2"></i>
                                  Ver Valores a Receber (Todos os Projetos)
                                </>
                              )}
                            </button>
                          </div>

                          {/* ALERTA */}
                          <div className="alert alert-warning mt-3 mb-0">
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>Atenção:</strong> Estas receitas não possuem projeto cadastrado. 
                            Clique no botão acima para ver todos os detalhes e vincular aos projetos corretos.
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* DISTRIBUIÇÃO RECEBIDO VS PREVISTO */}
              <div className="col-lg-4 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-chart-pie me-2 text-success"></i>
                      Recebido vs Previsto
                    </h5>
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Recebido', value: dados.estatisticas.total_receitas_recebidas || 0 },
                            { name: 'Previsto', value: dados.estatisticas.total_receitas_previstas || 0 }
                          ]}
                          cx="50%"
                          cy="50%"
                          outerRadius={120}
                          innerRadius={60}
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          <Cell fill="#00a126" />
                          <Cell fill="#ff2407" />
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* TABELA DE PROJETOS */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-table me-2 text-info"></i>
                      Lista Completa de Projetos
                    </h5>
                    <div>
                      <span className="badge bg-primary me-2">
                        {projetosFiltrados.length} projetos
                      </span>
                      {(filtroNomeProjeto || filtroCliente) && (
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setFiltroNomeProjeto('');
                            setFiltroCliente('');
                          }}
                        >
                          <i className="fas fa-times me-1"></i>
                          Limpar Filtros
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* LINHA DE FILTROS */}
                  <div className="card-body pb-0">
                    <div className="row g-2 mb-3">
                      <div className="col-md-3">
                        <input 
                          type="text" 
                          className="form-control form-control-sm" 
                          placeholder="Filtrar por nome do projeto..."
                          value={filtroNomeProjeto}
                          onChange={(e) => setFiltroNomeProjeto(e.target.value)}
                        />
                      </div>
                      <div className="col-md-3">
                        <input 
                          type="text" 
                          className="form-control form-control-sm" 
                          placeholder="Filtrar por cliente..."
                          value={filtroCliente}
                          onChange={(e) => setFiltroCliente(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 text-end">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Clique nos cabeçalhos para ordenar
                        </small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover table-striped mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th 
                              width="18%" 
                              style={{cursor: 'pointer'}}
                              onClick={() => handleOrdenacao('projeto')}
                            >
                              Projeto
                              {ordenacao.coluna === 'projeto' && (
                                <i className={`fas fa-sort-${ordenacao.direcao === 'desc' ? 'down' : 'up'} ms-1`}></i>
                              )}
                            </th>
                            <th 
                              width="10%" 
                              className="text-end"
                              style={{cursor: 'pointer'}}
                              onClick={() => handleOrdenacao('receitas')}
                            >
                              Receitas Total
                              {ordenacao.coluna === 'receitas' && (
                                <i className={`fas fa-sort-${ordenacao.direcao === 'desc' ? 'down' : 'up'} ms-1`}></i>
                              )}
                            </th>
                            <th 
                              width="10%" 
                              className="text-end"
                              style={{cursor: 'pointer'}}
                              onClick={() => handleOrdenacao('receitas_recebidas')}
                            >
                              Recebido
                              {ordenacao.coluna === 'receitas_recebidas' && (
                                <i className={`fas fa-sort-${ordenacao.direcao === 'desc' ? 'down' : 'up'} ms-1`}></i>
                              )}
                            </th>
                            <th 
                              width="10%" 
                              className="text-end"
                              style={{cursor: 'pointer'}}
                              onClick={() => handleOrdenacao('receitas_previstas')}
                            >
                              Previsto
                              {ordenacao.coluna === 'receitas_previstas' && (
                                <i className={`fas fa-sort-${ordenacao.direcao === 'desc' ? 'down' : 'up'} ms-1`}></i>
                              )}
                            </th>
                            <th 
                              width="10%" 
                              className="text-end"
                              style={{cursor: 'pointer'}}
                              onClick={() => handleOrdenacao('despesas')}
                            >
                              Despesas
                              {ordenacao.coluna === 'despesas' && (
                                <i className={`fas fa-sort-${ordenacao.direcao === 'desc' ? 'down' : 'up'} ms-1`}></i>
                              )}
                            </th>
                            <th 
                              width="10%" 
                              className="text-end"
                              style={{cursor: 'pointer'}}
                              onClick={() => handleOrdenacao('saldo')}
                            >
                              Saldo
                              {ordenacao.coluna === 'saldo' && (
                                <i className={`fas fa-sort-${ordenacao.direcao === 'desc' ? 'down' : 'up'} ms-1`}></i>
                              )}
                            </th>
                            <th 
                              width="8%" 
                              className="text-center"
                              style={{cursor: 'pointer'}}
                              onClick={() => handleOrdenacao('margem')}
                            >
                              Margem
                              {ordenacao.coluna === 'margem' && (
                                <i className={`fas fa-sort-${ordenacao.direcao === 'desc' ? 'down' : 'up'} ms-1`}></i>
                              )}
                            </th>
                            <th 
                              width="8%" 
                              className="text-center"
                              style={{cursor: 'pointer'}}
                              onClick={() => handleOrdenacao('percentual_recebido')}
                            >
                              % Recebido
                              {ordenacao.coluna === 'percentual_recebido' && (
                                <i className={`fas fa-sort-${ordenacao.direcao === 'desc' ? 'down' : 'up'} ms-1`}></i>
                              )}
                            </th>
                            <th width="8%" className="text-center">Movimentos</th>
                            <th width="8%" className="text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projetosFiltrados.length > 0 ? (
                            projetosFiltrados.map((projeto, idx) => (
                              <tr key={idx}>
                                <td>
                                  <strong>{projeto.projeto}</strong>
                                  <br />
                                  <small className="text-muted">
                                    <i className="fas fa-calendar me-1"></i>
                                    {projeto.data_inicio} até {projeto.data_fim}
                                  </small>
                                  <br />
                                  <small className="text-primary">
                                    <i className="fas fa-user me-1"></i>
                                    {projeto.principal_cliente}
                                  </small>
                                </td>
                                <td className="text-end">
                                  <strong className="text-primary">{formatarValor(projeto.receitas)}</strong>
                                  <br />
                                  <small className="text-muted">{projeto.qtd_receitas} mov.</small>
                                </td>
                                <td className="text-end">
                                  <strong className="text-success">{formatarValor(projeto.receitas_recebidas)}</strong>
                                  <br />
                                  <small className="text-muted">{projeto.qtd_receitas_recebidas} mov.</small>
                                </td>
                                <td className="text-end">
                                  <strong className="text-warning">{formatarValor(projeto.receitas_previstas)}</strong>
                                  <br />
                                  <small className="text-muted">{projeto.qtd_receitas_previstas} mov.</small>
                                </td>
                                <td className="text-end">
                                  <strong className="text-danger">{formatarValor(projeto.despesas)}</strong>
                                  <br />
                                  <small className="text-muted">{projeto.qtd_despesas} mov.</small>
                                </td>
                                <td className="text-end">
                                  <strong className={projeto.saldo >= 0 ? 'text-success' : 'text-danger'}>
                                    {formatarValor(projeto.saldo)}
                                  </strong>
                                </td>
                                <td className="text-center">
                                  <span className={`badge ${
                                    projeto.margem >= 20 ? 'bg-success' :
                                    projeto.margem >= 10 ? 'bg-info' :
                                    projeto.margem >= 0 ? 'bg-warning' : 'bg-danger'
                                  }`}>
                                    {projeto.margem.toFixed(1)}%
                                  </span>
                                </td>
                                <td className="text-center">
                                  <div className="progress" style={{height: '20px'}}>
                                    <div 
                                      className="progress-bar bg-success" 
                                      role="progressbar" 
                                      style={{width: `${projeto.percentual_recebido}%`}}
                                      aria-valuenow={projeto.percentual_recebido} 
                                      aria-valuemin="0" 
                                      aria-valuemax="100"
                                    >
                                      <small style={{fontSize: '10px'}}>{projeto.percentual_recebido.toFixed(0)}%</small>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <span className="badge bg-primary">
                                    {projeto.qtd_movimentos}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => carregarDetalhesProjeto(projeto.projeto)}
                                    title="Ver Detalhes"
                                    disabled={carregandoDetalhes}
                                  >
                                    {carregandoDetalhes ? (
                                      <span className="spinner-border spinner-border-sm"></span>
                                    ) : (
                                      <i className="fas fa-eye"></i>
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="10" className="text-center py-4">
                                <i className="fas fa-search fa-2x text-muted mb-2"></i>
                                <p className="text-muted mb-0">Nenhum projeto encontrado com os filtros aplicados</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* RODAPÉ COM TOTALIZADORES */}
                  {projetosFiltrados.length > 0 && (
                    <div className="card-footer bg-light">
                      <div className="row text-center">
                        <div className="col-md-3">
                          <small className="text-muted d-block">Total Receitas</small>
                          <strong className="text-success">
                            {formatarValor(projetosFiltrados.reduce((acc, p) => acc + p.receitas, 0))}
                          </strong>
                        </div>
                        <div className="col-md-3">
                          <small className="text-muted d-block">Total Recebido</small>
                          <strong className="text-success">
                            {formatarValor(projetosFiltrados.reduce((acc, p) => acc + p.receitas_recebidas, 0))}
                          </strong>
                        </div>
                        <div className="col-md-3">
                          <small className="text-muted d-block">Total Previsto</small>
                          <strong className="text-warning">
                            {formatarValor(projetosFiltrados.reduce((acc, p) => acc + p.receitas_previstas, 0))}
                          </strong>
                        </div>
                        <div className="col-md-3">
                          <small className="text-muted d-block">Saldo Total</small>
                          <strong className={projetosFiltrados.reduce((acc, p) => acc + p.saldo, 0) >= 0 ? 'text-success' : 'text-danger'}>
                            {formatarValor(projetosFiltrados.reduce((acc, p) => acc + p.saldo, 0))}
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* TOP CLIENTES E CATEGORIAS */}
            <div className="row mb-4">
              {/* TOP 10 CLIENTES (RECEITAS) */}
              <div className="col-lg-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-users me-2 text-success"></i>
                 Fronecedores (Receitas)
                    </h5>
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={dados.top_clientes_receitas} margin={{ top: 5, right: 10, left: 10, bottom: 100 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="cliente" tick={{ fontSize: 10, angle: -40, textAnchor: 'end' }} interval={0} />
                        <YAxis tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 11 }} width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="valor" fill="#28a745" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* TODAS AS CATEGORIAS DE DESPESA */}
              <div className="col-lg-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-arrow-down me-2 text-danger"></i>
                      Categorias de Despesa 
                    </h5>
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={dados.todas_categorias_despesas} margin={{ top: 5, right: 10, left: 10, bottom: 100 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="categoria" tick={{ fontSize: 10, angle: -40, textAnchor: 'end' }} interval={0} />
                        <YAxis tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 10 }} width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="valor" fill="#dc3545" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* MODAL DETALHES */}
        {modalDetalhes && projetoDetalhes && (
          <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.7)'}} onClick={() => setModalDetalhes(false)}>
            <div className="modal-dialog modal-fullscreen-lg-down modal-dialog-scrollable" style={{maxWidth: '95%'}} onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef'}}>
                  <h5 className="modal-title mb-0 fw-bold" style={{color: '#212529'}}>
                    <i className="fas fa-project-diagram me-2"></i>
                    Detalhes: {projetoDetalhes.projeto}
                  </h5>
                  <div className="d-flex gap-2">
                    {projetoDetalhes.projeto === 'Sem dados do projeto' && (
                      <button 
                        type="button" 
                        className="btn btn-warning btn-sm"
                        onClick={() => setEditandoProjeto(!editandoProjeto)}
                      >
                        <i className={`fas fa-${editandoProjeto ? 'times' : 'edit'} me-1`}></i>
                        {editandoProjeto ? 'Cancelar' : 'Editar Projetos'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setModalDetalhes(false);
                        setEditandoProjeto(false);
                      }}
                    ></button>
                  </div>
                </div>
                
                {projetoDetalhes.resumo && (
                  <div className="modal-header bg-light">
                    <div className="w-100">
                      <div className="row text-center">
                        <div className="col-md-2">
                          <small className="text-muted d-block">Período</small>
                          <h6 className="text-primary mb-0" style={{fontSize: '11px'}}>
                            {projetoDetalhes.resumo.periodo_inicio} - {projetoDetalhes.resumo.periodo_fim}
                          </h6>
                        </div>
                        <div className="col-md-2">
                          <small className="text-muted d-block">Receitas</small>
                          <h6 className="text-success mb-0">
                            {formatarValor(projetoDetalhes.resumo.total_receitas)}
                          </h6>
                          <small className="text-muted">{projetoDetalhes.resumo.qtd_receitas} mov.</small>
                        </div>
                        <div className="col-md-2">
                          <small className="text-muted d-block">Despesas</small>
                          <h6 className="text-danger mb-0">
                            {formatarValor(projetoDetalhes.resumo.total_despesas)}
                          </h6>
                          <small className="text-muted">{projetoDetalhes.resumo.qtd_despesas} mov.</small>
                        </div>
                        <div className="col-md-2">
                          <small className="text-muted d-block">Saldo</small>
                          <h6 className={`mb-0 ${projetoDetalhes.resumo.saldo >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatarValor(projetoDetalhes.resumo.saldo)}
                          </h6>
                        </div>
                        <div className="col-md-2">
                          <small className="text-muted d-block">Margem</small>
                          <h6 className={`mb-0 ${projetoDetalhes.resumo.margem >= 0 ? 'text-success' : 'text-danger'}`}>
                            {projetoDetalhes.resumo.margem.toFixed(1)}%
                          </h6>
                        </div>
                        <div className="col-md-2">
                          <small className="text-muted d-block">Total Movimentos</small>
                          <h6 className="text-primary mb-0">
                            {projetoDetalhes.resumo.qtd_movimentos}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
                  {editandoProjeto && (
                    <div className="alert alert-warning">
                      <i className="fas fa-edit me-2"></i>
                      <strong>Modo de Edição Ativado</strong>
                      <p className="mb-0 mt-2">
                        <i className="fas fa-keyboard me-2"></i>
                        Digite o nome do projeto em cada linha e clique em "Salvar" para atualizar na Omie. 
                        Você pode pressionar <kbd>Enter</kbd> para salvar rapidamente.
                      </p>
                    </div>
                  )}

                  {projetoDetalhes.evolucao && projetoDetalhes.evolucao.length > 0 && !editandoProjeto && (
                    <div className="mb-4">
                      <h6 className="text-muted mb-3">
                        <i className="fas fa-chart-line me-2"></i>
                        Evolução Mensal
                      </h6>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={projetoDetalhes.evolucao}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="mes" />
                          <YAxis tickFormatter={(value) => formatarValor(value)} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar dataKey="receitas" name="Receitas" fill="#28a745" />
                          <Bar dataKey="despesas" name="Despesas" fill="#dc3545" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <h6 className="text-muted mb-3">
                    <i className="fas fa-list me-2"></i>
                    Lista de Movimentos ({projetoDetalhes.movimentos.length})
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-hover table-sm">
                      <thead className="table-light sticky-top">
                        <tr>
                          {/* NOVA COLUNA: NÚMERO (APENAS PARA VALORES A RECEBER) */}
                          {projetoDetalhes.projeto === 'Valores a Receber (Previstos)' && (
                            <th width="5%">Nº</th>
                          )}
                          <th width="8%">Data</th>
                          <th width="18%">Descrição</th>
                          {/* MOSTRAR COLUNA PROJETO APENAS PARA "VALORES A RECEBER" */}
                          {projetoDetalhes.projeto === 'Valores a Receber (Previstos)' && (
                            <th width="13%">Projeto</th>
                          )}
                          <th width="12%">Categoria</th>
                          <th width="12%">Cliente</th>
                          <th width="10%" className="text-end">Valor</th>
                          <th width="6%" className="text-center">Tipo</th>
                          <th width="8%" className="text-center">Status</th>
                          {editandoProjeto && <th width="20%">Novo Projeto</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {projetoDetalhes.movimentos.map((mov, idx) => (
                          <MovimentoRow 
                            key={idx} 
                            movimento={mov} 
                            editando={editandoProjeto}
                            onSalvar={atualizarNomeProjeto}
                            mostrarProjeto={projetoDetalhes.projeto === 'Valores a Receber (Previstos)'}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setModalDetalhes(false);
                      setEditandoProjeto(false);
                    }}
                  >
                    <i className="fas fa-times me-1"></i>
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className="row mt-4 mb-3">
          <div className="col-12">
            <div className="card border-0" style={{backgroundColor: '#00205b'}}>
              <div className="card-body py-3">
                <div className="text-center">
                  <p className="mb-0" style={{color: '#ffffff', fontSize: '0.9rem'}}>
                    <i className="fas fa-project-diagram me-2"></i>
                    <strong>Gestão de Projetos ERNEDU</strong>
                    <span className="mx-2">|</span>
                    Financial Management Platform © 2025
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjetosErnedu;